import { Autocomplete, Box, Divider, FormLabel, TextField } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { getParts } from "../../api/parts";
import { getServices } from "../../api/services";
import InvoiceBillingItemsPartsCard from "./billingItemsWithoutWorks/InvoiceBillingItemsPartsCard";
import InvoiceBillingItemsServicesCard from "./billingItemsWithoutWorks/InvoiceBillingItemsServicesCard";
import Decimal from "decimal.js";
import ErrorBox from "../../../components/shared/boxes/ErrorBox";

const InvoiceBillingItemsWithoutWorks = ({ loadingParts, onValidityChange, loadingServices, setPartsValue, setWorksValue, onInvoicePartsChange,
    onInvoiceServicesChange, data, setData }) => {
    const [partsOptions, setPartsOptions] = useState([]);
    const [servicesOptions, setServicesOptions] = useState([]);
    const [selectedPart, setSelectedPart] = useState(null);
    const [selectedService, setSelectedService] = useState(null);
    const [partsSearchText, setPartsSearchText] = useState("");
    const [servicesSearchText, setServicesSearchText] = useState("");
    const [loadingPartsSearch, setLoadingPartsSearch] = useState(false);
    const [loadingServicesSearch, setLoadingServicesSearch] = useState(false);
    const [configPartsApiError, setConfigPartsApiError] = useState(null);
    const [configServicesApiError, setConfigServicesApiError] = useState(null);
    const cards = [...(data.partsCards || []), ...(data.servicesCards || [])];

    useEffect(() => {
        const fetchParts = async () => {
            setLoadingPartsSearch(true);
            try {
                const urlParams = new URLSearchParams();
                urlParams.append('status', 'active');

                if (partsSearchText.trim() !== "") {
                    urlParams.append('q', partsSearchText.trim());
                }

                const result = await getParts({ urlParams });
                const options = Array.isArray(result?.results)
                    ? result.results.map(p => ({ label: p.name, price: p.price, id: p.id }))
                    : [];

                setPartsOptions(options);
            } catch (error) {
                setConfigPartsApiError(error.message);
                setPartsOptions([]);
            } finally {
                setLoadingPartsSearch(false);
            }
        };

        const timeoutId = setTimeout(fetchParts, 1000);
        return () => clearTimeout(timeoutId);
    }, [partsSearchText]);

    useEffect(() => {
        const fetchServices = async () => {
            setLoadingServicesSearch(true);
            try {
                const urlParams = new URLSearchParams();
                urlParams.append('status', 'active');

                if (servicesSearchText.trim() !== "") {
                    urlParams.append('q', servicesSearchText.trim());
                }

                const result = await getServices({ urlParams });
                const options = Array.isArray(result?.results)
                    ? result.results.map(s => ({ label: s.name, price: s.price, id: s.id }))
                    : [];

                setServicesOptions(options);
            } catch (error) {
                setConfigServicesApiError(error.message);
                setServicesOptions([]);
            } finally {
                setLoadingServicesSearch(false);
            }
        };

        const timeoutId = setTimeout(fetchServices, 1000);
        return () => clearTimeout(timeoutId);
    }, [servicesSearchText]);

    const handleAddCard = (item, type) => {
        if (!item) return;
        const targetKey = type === "part" ? "partsCards" : "servicesCards";
        if (data[targetKey].some(c => c.id === item.id)) return;

        const newCard = { ...item, type, quantity: 1 };
        setData(prev => ({ ...prev, [targetKey]: [...prev[targetKey], newCard] }));

        if (type === 'part') setSelectedPart(null);
        else setSelectedService(null);
    };

    const handleRemoveCard = (id) => {
        setData(prev => ({
            ...prev,
            partsCards: prev.partsCards.filter(c => c.id !== id),
            servicesCards: prev.servicesCards.filter(c => c.id !== id)
        }));
    };

    const handleQuantityChange = (index, type, quantity) => {
        if (type === 'part') {
            setData(prev => ({
                ...prev,
                partsCards: prev.partsCards.map((c, i) =>
                    i === index ? { ...c, quantity } : c
                )
            }));
        } else if (type === 'service') {
            setData(prev => ({
                ...prev,
                servicesCards: prev.servicesCards.map((c, i) =>
                    i === index ? { ...c, quantity } : c
                )
            }));
        }
    };

    const availableParts = partsOptions.filter(
        p => !cards.some(c => c.id === p.id && c.type === 'part')
    );

    const availableServices = servicesOptions.filter(
        s => !cards.some(c => c.id === s.id && c.type === 'service')
    );

    const totals = useMemo(() => {
        let parts = new Decimal(0);
        let services = new Decimal(0);

        for (const c of data.partsCards) {
            parts = parts.plus(new Decimal(c.price || 0).times(c.quantity));
        }
        for (const c of data.servicesCards) {
            services = services.plus(new Decimal(c.price || 0).times(c.quantity));
        }

        return { parts: parts.toNumber(), services: services.toNumber() };
    }, [data.partsCards, data.servicesCards]);

    useEffect(() => {
        setPartsValue?.(totals.parts);
        setWorksValue?.(totals.services);

        const invoiceParts = data.partsCards.map(c => ({
            part_id: c.id,
            quantity: new Decimal(c.quantity).toFixed(2),
            unit_value: c.price ? new Decimal(c.price).toFixed(2) : undefined
        }));
        const invoiceServices = data.servicesCards.map(c => ({
            service_id: c.id,
            quantity: new Decimal(c.quantity).toFixed(2),
            unit_value: c.price ? new Decimal(c.price).toFixed(2) : undefined
        }));

        onInvoicePartsChange?.(invoiceParts);
        onInvoiceServicesChange?.(invoiceServices);

        const isValid = (data.partsCards.length + data.servicesCards.length) > 0
            && !configPartsApiError
            && !configServicesApiError;
        onValidityChange?.(isValid);
    }, [data.partsCards, data.servicesCards, totals, setPartsValue, setWorksValue, onInvoicePartsChange, onInvoiceServicesChange, onValidityChange, configPartsApiError, configServicesApiError]);

    return (
        <>
            <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
                {(configPartsApiError || configServicesApiError) && (
                    <ErrorBox
                        errorMessage={configPartsApiError || configServicesApiError}
                        onClose={() => {
                            setConfigPartsApiError(null);
                            setConfigServicesApiError(null);
                        }}
                    />
                )}
                < Box sx={{ px: '1.5rem', pt: '3rem', gap: '2.5rem', mb: '1.5rem' }}>
                    <FormLabel sx={{ color: 'text.primary' }}><b> Peças e Materiais para cobrança </b></FormLabel>
                    <Autocomplete
                        disablePortal
                        options={availableParts}
                        getOptionLabel={(option) => option?.label || ''}
                        value={selectedPart || null}
                        onChange={(e, newValue) => {
                            setSelectedPart(newValue);
                            if (newValue) {
                                handleAddCard(newValue, 'part');
                                setSelectedPart(null);
                                setPartsSearchText('');
                            }
                        }}
                        inputValue={partsSearchText}
                        onInputChange={(e, newInputValue) => setPartsSearchText(newInputValue)}
                        noOptionsText={!loadingPartsSearch ? "Nenhum resultado encontrado" : "Carregando..."}
                        slotProps={{
                            listbox: {
                                sx: {
                                    maxHeight: '13rem',
                                },
                            },
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                placeholder='Pesquisar'
                            />
                        )}
                    />
                </Box>
                {
                    cards.filter(c => c.type === "part").map((part, index) => (
                        <InvoiceBillingItemsPartsCard
                            key={part.id}
                            partsId={part.id}
                            partsName={part.label}
                            partsValue={part.price}
                            quantity={part.quantity}
                            onRemove={() => handleRemoveCard(part.id)}
                            loading={loadingParts}
                            onQuantityChange={(q) => handleQuantityChange(index, 'part', q)}
                        />
                    ))
                }
                <Divider sx={{ m: '3rem 1.5rem' }} />
                <Box sx={{ px: '1.5rem', mb: '1.5rem'}}>
                    <FormLabel sx={{ color: 'text.primary' }}><b> Serviços para cobrança </b></FormLabel>
                    <Autocomplete
                        disablePortal
                        options={availableServices}
                        getOptionLabel={(option) => option?.label || ''}
                        value={selectedService || null}
                        onChange={(e, newValue) => {
                            setSelectedService(newValue);
                            if (newValue) {
                                handleAddCard(newValue, 'service');
                                setSelectedService(null);
                                setServicesSearchText('');
                            }
                        }}
                        inputValue={servicesSearchText}
                        onInputChange={(e, newInputValue) => setServicesSearchText(newInputValue)}
                        noOptionsText={!loadingServicesSearch ? "Nenhum resultado encontrado" : "Carregando..."}
                        slotProps={{
                            listbox: {
                                sx: {
                                    maxHeight: '13rem',
                                },
                            },
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                placeholder='Pesquisar'
                            />
                        )}
                    />
                </Box>
                {cards.filter(c => c.type === "service").map((service, index) => (
                    <InvoiceBillingItemsServicesCard
                        key={service.id}
                        servicesId={service.id}
                        servicesName={service.label}
                        quantity={service.quantity}
                        servicesValue={service.price}
                        onRemove={() => handleRemoveCard(service.id)}
                        loading={loadingServices}
                        onQuantityChange={(q) => handleQuantityChange(index, 'service', q)}
                    />
                ))}
            </Box >
        </>
    )
}

export default InvoiceBillingItemsWithoutWorks;
